import { api } from '@packages/backend/convex/_generated/api';
import { printJobStrictSchema } from '@packages/shared';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from 'convex-helpers/react';
import { TriangleAlert } from 'lucide-react';
import { ErrorAlert } from '@/components/ErrorAlert';
import { LoadingIcon } from '@/components/LoadingIcon';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { SuperAdminOnly } from '@/features/auth/SuperAdminOnly';
import { useFeatureFlagEnabled } from '@/features/feature-flags/useFeatureFlagEnabled';
import { PrintJobDetails } from './PrintJobDetails';
import { PrintJobsList } from './PrintJobsList';

export const Route = createFileRoute('/r/$restaurantId/print-jobs')({
  validateSearch: printJobStrictSchema.pick({ _id: true }),
  component: PrintJobsPage,
});

function PrintJobsPage() {
  const { restaurantId } = Route.useParams();
  const { _id } = Route.useSearch();
  const isPrintJobLoggingEnabled = useFeatureFlagEnabled(restaurantId, 'print-job-logging');
  const {
    data: printJobs,
    error,
    isError,
    isPending,
  } = useQuery(
    api.printJobs.listByRestaurantId,
    isPrintJobLoggingEnabled ? { restaurantId } : 'skip',
  );

  if (!isPrintJobLoggingEnabled) {
    return (
      <Alert className="md:w-1/2" variant="destructive">
        <TriangleAlert className="h-4 w-4" />
        <AlertTitle>Print logging is disabled</AlertTitle>
        <AlertDescription>
          <SuperAdminOnly>
            <Link
              className={'underline'}
              params={{ restaurantId }}
              to={'/r/$restaurantId/s/feature-flags'}
            >
              Feature flags
            </Link>
          </SuperAdminOnly>
        </AlertDescription>
      </Alert>
    );
  }
  if (isError) return <ErrorAlert message={error.message} title="Error loading print jobs" />;
  if (isPending) return <LoadingIcon />;

  const selectedPrintJob = printJobs.find((pj) => pj._id === _id) ?? null;

  return (
    <div className="grid md:grid-cols-2">
      {/* Left column */}
      <div className="md:pr-3">
        <PrintJobsList printJobs={printJobs} selectedPrintJobId={_id} />
      </div>

      {/* Right column */}
      <div className="md:pl-3">
        {selectedPrintJob ? (
          <PrintJobDetails printJob={selectedPrintJob} />
        ) : (
          <div className="text-muted-foreground text-center mt-8">
            Select a print job to view details
          </div>
        )}
      </div>
    </div>
  );
}
